import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { TCPAConsent } from './TCPAConsent';
const startDateOptions = ['Immediately', 'Within 1 month', 'Within 3 months', 'Within 6 months', 'Within 1 year', 'Not sure yet'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 30; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
export function HomeInlineLeadCapture() {
  const navigate = useNavigate();
  const [formStep, setFormStep] = useState(1);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    if (validateUniStep2()) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = () => {
    if (validateUniStep3()) {
      const cleanedFormData = {
        ...formData,
        phone: stripPhone(formData.phone)
      };
      console.log('Lead submitted:', cleanedFormData);
      navigate('/thank-you', {
        state: {
          formData: cleanedFormData
        }
      });
    }
  };
  const totalSteps = 3;
  const progressPercent = Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const renderFormContent = () => {
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-222">
          <div data-id="element-223">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-224">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-225">
              <option value="" data-id="element-226">-- Please Select --</option>
              {educationLevels.map(level => <option key={level} value={level} data-id="element-227">
                  {level}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-228">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-229">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-230">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-231">
              <option value="" data-id="element-232">-- Please Select --</option>
              {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-233">
                  {opt}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-234">{errors.startDate}</p>}
          </div>

          <div data-id="element-235">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-236">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-237">
              <option value="" data-id="element-238">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-239">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-240">{errors.gradYear}</p>}
          </div>

          <div className="pt-2" data-id="element-241">
            <button type="button" onClick={handleStep1Next} className="w-full hover:bg-[#047857] text-white font-bold py-4 px-6 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl bg-[#D4A943]" data-id="element-242">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-243">
          <div data-id="element-244">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-245">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-246" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-247">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-248">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-249" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-250">
                <div data-id="element-251">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-252">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="City" data-id="element-253" />
                </div>
                <div data-id="element-254">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-255">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="State" data-id="element-256" />
                </div>
              </div>
              <div data-id="element-257">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-258">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal normal-case" data-id="element-259">
                    (optional)
                  </span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-260" />
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-261">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-262">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-263" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-[#059668] hover:bg-[#047857] text-white font-bold py-4 px-6 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl" data-id="element-264">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-265">
          <div className="grid grid-cols-2 gap-3" data-id="element-266">
            <div data-id="element-267">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-268">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-269" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-270">{errors.firstName}</p>}
            </div>
            <div data-id="element-271">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-272">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-273" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-274">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-275">
            <div data-id="element-276">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-277">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-278" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-279">{errors.email}</p>}
            </div>
            <div data-id="element-280">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-281">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-282" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-283">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2" data-id="element-284">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-285">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-navy focus:ring-navy cursor-pointer flex-shrink-0" data-id="element-286" />
              <span className="text-xs text-gray-600 leading-relaxed" data-id="element-287">
                By submitting this form, you are giving your express written
                consent to be contacted regarding our services using email,
                telephone or text, including our use of automated technology for
                calls or texts to the number provided above.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-288">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-289">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-290">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-291" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} className="flex-1 bg-[#A52A2A] hover:bg-[#8B1A1A] text-white font-bold py-4 px-6 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl" data-id="element-292">
              Get My Results
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 sticky top-6 ring-1 ring-gray-900/5" data-id="element-293">
      <div className="px-6 py-4 bg-[#1B365D]" data-id="element-294">
        <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-295">
          Complete Your Profile
        </h2>
        <p className="text-gray-300 text-xs text-center mt-1" data-id="element-296">
          Get matched with the best programs
        </p>
      </div>

      <div className="px-6 pt-4" data-id="element-297">
        <div className="flex justify-between items-center mb-1.5" data-id="element-298">
          <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-299">
            Step {formStep} of {totalSteps}
          </span>
          <span className="text-xs text-gray-500" data-id="element-300">{progressPercent}%</span>
        </div>
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-301">
          <motion.div className="h-full rounded-full bg-[#A52A2A]" initial={{
          width: 0
        }} animate={{
          width: `${progressPercent}%`
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} data-id="element-302" />
        </div>
      </div>

      <div className="p-6" data-id="element-303">
        <AnimatePresence mode="wait" data-id="element-304">
          <motion.div key={formStep} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-305">
            {renderFormContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}